import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';

import type { DirtyAware } from '../forms/dirty-aware';
import { DialogService } from '../../ui/primitives/dialog';
import {
  UnsavedChangesDialogComponent,
  type UnsavedChangesDialogData,
  type UnsavedChangesResult,
} from '../../ui/primitives/unsaved-changes-dialog';

/**
 * Dirty-form CanDeactivate guard (Phase H Stage 3 / ADR-0047).
 *
 * Routes that own a form opt in via:
 *
 *   { path: ..., component: SomeForm, canDeactivate: [dirtyFormGuard] }
 *
 * The route component must implement `DirtyAware`. When the user attempts
 * to navigate away with `isDirty()` returning true, the guard opens
 * `UnsavedChangesDialog`. The three results map to:
 *
 *   - **save**    → call `component.save()`. Navigation proceeds iff
 *                   save returned true. A failed save (validation, server
 *                   error) returns the user to the page so they can see
 *                   inline errors.
 *   - **discard** → navigation proceeds; local edits are dropped.
 *   - **cancel**  → navigation is blocked.
 *
 * If the component has no `save()` method, the dialog hides the Save
 * button and only Discard / Cancel are offered.
 *
 * Browser-level navigation (back, refresh, tab close) is caught by the
 * separate `beforeunload` handler mounted on `AppShellLayout` — this
 * guard only intercepts in-app router navigation.
 */
export const dirtyFormGuard: CanDeactivateFn<DirtyAware> = async (component) => {
  if (!component?.isDirty?.()) return true;

  const dialogService = inject(DialogService);
  const handle = dialogService.open<
    UnsavedChangesDialogComponent,
    UnsavedChangesDialogData,
    UnsavedChangesResult
  >(UnsavedChangesDialogComponent, {
    disableClose: true,
    data: { canSave: typeof component.save === 'function' },
  });

  const result = await new Promise<UnsavedChangesResult | undefined>((resolve) => {
    handle.afterClosed().subscribe((value) => resolve(value));
  });

  if (result === 'cancel' || result === undefined) return false;
  if (result === 'discard') return true;
  // 'save': only valid when component.save exists (canSave gated the button).
  if (!component.save) return false;
  return await component.save();
};
